import React from 'react';
import { LoadingSpinner } from 'pepsico-ds';

/**
 * StatsDisplay component for displaying a row of statistics with optional trend indicators.
 * 
 * @param {Object} props - Component props
 * @param {Array<Object>} props.items - Array of stat items to display
 * @param {string|number} props.items[].value - The main statistic value to display (e.g., number, 'Error').
 * @param {string} props.items[].label - Label text for the statistic
 * @param {boolean} [props.items[].isLoading=false] - If true, displays a loading spinner instead of the value.
 * @param {string} [props.items[].color] - Custom color for the statistic value (overrides props.color).
 * @param {Object} [props.items[].trend] - Optional trend information
 * @param {('up'|'down')} [props.items[].trend.direction] - Direction of the trend
 * @param {string|number} [props.items[].trend.value] - Value of the trend
 * @param {('small'|'default')} [props.size='default'] - Size variant for the component
 * @param {string} [props.color] - Default custom color for the statistic value (h3 element).
 * @returns {JSX.Element} The rendered StatsDisplay component
 */
export default function StatsDisplay({ items, size = 'default', color }) {
  /**
   * Formats large numbers with appropriate suffixes (k, M, B)
   * @param {number|string} num - The number or string (like 'Error') to format/display
   * @returns {string} Formatted number with suffix or the original string
   */
  const formatNumber = (num) => {
    if (typeof num !== 'number') return num; // Return non-numbers (like 'Error') as is
    
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return num.toString();
  };

  return (
    <div className='flex flex-row justify-start'>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <div className='w-[1px] bg-[#4E4E4E] h-auto my-1'></div>}
          <div className='flex flex-col px-4'>
            {/* Conditionally render LoadingSpinner based on item.isLoading */}
            {item.isLoading ? (
              <div className={`flex items-center justify-start ${size === 'small' ? 'h-[36px]' : 'h-[60px]'}`}> {/* Adjust height and center spinner */}
                <LoadingSpinner
                  size={size === 'small' ? { height: '20px', width: '20px' } : { height: '30px', width: '30px' }}
                />
              </div>
            ) : (
              <h3 className={`${size === 'small' ? 'text-[24px]' : 'text-[40px]'} ${item.color || color || 'text-white'} font-[700]`}>
                {/* Display the value (could be number or 'Error') */}
                {formatNumber(item.value)}
              </h3>
            )}
            <p className='text-[14px] text-[#D0D0D0]'>{item.label}</p>
            {/* Render trend only if not loading */}
            {!item.isLoading && item.trend && (
              <div className={`flex items-center ${item.trend.direction === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                {item.trend.direction === 'up' ? '↑' : '↓'} {item.trend.value}
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}